/**
 * Extra Materials Page
 *
 * Publish extra materials / homework links. Available to the director and to
 * every teacher (each teacher manages their own; the director sees all).
 * Each material is a link (Drive / activity site) scoped to all students,
 * every class of a level, or specific classes.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SearchableSelect, Spinner } from "../../components/ui";
import type { SearchableSelectItem } from "../../components/ui";
import { useTeacherClasses } from "../../hooks/useTeacher";
import {
  useCreateExtraMaterial,
  useDeleteExtraMaterial,
  useExtraMaterials,
  useUpdateExtraMaterial,
} from "../../hooks/useExtraMaterials";
import { useAuthStore } from "../../store";
import type {
  CreateExtraMaterialRequest,
  ExtraMaterial,
} from "../../types/material";

const emptyForm: CreateExtraMaterialRequest = {
  title: "",
  description: "",
  externalUrl: "",
  materialType: "homework",
  scope: "all",
  levelCode: "",
  classGroups: [],
};

export function AdminExtraMaterialsPage() {
  const { user } = useAuthStore();
  // Director = staff user (matches DirectorRoute and the backend's IsDirector).
  const isDirector = !!user?.isStaff;
  const backTo = isDirector ? "/admin" : "/teacher";

  const { data: materials, isLoading } = useExtraMaterials();
  // Role-aware sources: director sees all classes, a teacher sees their own.
  const { data: classes } = useTeacherClasses();
  const createMaterial = useCreateExtraMaterial();
  const updateMaterial = useUpdateExtraMaterial();
  const deleteMaterial = useDeleteExtraMaterial();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selected, setSelected] = useState<ExtraMaterial | null>(null);
  const [form, setForm] = useState<CreateExtraMaterialRequest>({
    ...emptyForm,
    scope: isDirector ? "all" : "class",
  });
  const [toDelete, setToDelete] = useState<ExtraMaterial | null>(null);

  // Director-only filters
  const [filterCreator, setFilterCreator] = useState("");
  const [filterClass, setFilterClass] = useState("");

  const classItems = useMemo<SearchableSelectItem[]>(
    () =>
      (classes ?? []).map((c) => ({
        id: c.id,
        label: `${c.name} (${c.levelCode})`,
        searchText: `${c.name} ${c.levelCode}`,
      })),
    [classes],
  );

  // Levels available for the LEVEL scope, derived from the user's classes.
  const levelCodes = useMemo(
    () =>
      Array.from(
        new Set((classes ?? []).map((c) => c.levelCode).filter(Boolean)),
      ).sort(),
    [classes],
  );

  // Filter options derived from existing materials (only what's actually used).
  const creatorOptions = useMemo(
    () =>
      Array.from(
        new Set(
          (materials ?? [])
            .map((m) => m.createdByName)
            .filter((n): n is string => !!n),
        ),
      ).sort(),
    [materials],
  );

  const classFilterOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of materials ?? []) {
      (m.classGroups ?? []).forEach((id, i) => {
        if (id) map.set(id, m.classGroupNames?.[i] ?? id);
      });
    }
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [materials]);

  const filteredMaterials = useMemo(
    () =>
      (materials ?? []).filter((m) => {
        if (filterCreator && m.createdByName !== filterCreator) return false;
        if (filterClass && !(m.classGroups ?? []).includes(filterClass))
          return false;
        return true;
      }),
    [materials, filterCreator, filterClass],
  );

  const openCreate = () => {
    setForm({ ...emptyForm, scope: isDirector ? "all" : "class" });
    setIsEditing(false);
    setSelected(null);
    setIsModalOpen(true);
  };

  const openEdit = (m: ExtraMaterial) => {
    setForm({
      title: m.title,
      description: m.description,
      externalUrl: m.externalUrl,
      materialType: m.materialType,
      scope: m.scope,
      levelCode: m.levelCode ?? "",
      classGroups: m.classGroups ?? [],
    });
    setSelected(m);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setSelected(null);
    setForm({ ...emptyForm, scope: isDirector ? "all" : "class" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: CreateExtraMaterialRequest = {
      title: form.title,
      description: form.description,
      externalUrl: form.externalUrl,
      materialType: form.materialType,
      scope: form.scope,
      levelCode: form.scope === "level" ? form.levelCode : null,
      classGroups: form.scope === "class" ? form.classGroups : [],
    };
    if (isEditing && selected) {
      await updateMaterial.mutateAsync({ id: selected.id, data: payload });
    } else {
      await createMaterial.mutateAsync(payload);
    }
    closeModal();
  };

  const isSaving = createMaterial.isPending || updateMaterial.isPending;

  const handleDelete = async () => {
    if (!toDelete) return;
    await deleteMaterial.mutateAsync(toDelete.id);
    setToDelete(null);
  };

  const describeScope = (m: ExtraMaterial) => {
    if (m.scope === "level") return `Level ${m.levelCode ?? ""}`.trim();
    if (m.scope === "class")
      return m.classGroupNames && m.classGroupNames.length > 0
        ? m.classGroupNames.join(", ")
        : "Class";
    return "All students";
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6">
          <Link
            to={backTo}
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-2 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
                Extra Materials
              </h1>
              <p className="text-earth-700 text-sm mt-1">
                Publish homework / extra links to all students, a level, or a class
              </p>
            </div>
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              <i className="ri-add-line"></i>
              New Material
            </button>
          </div>
        </div>

        {/* Filters (director only) */}
        {isDirector && (materials?.length ?? 0) > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1 text-xs font-medium text-earth-700 uppercase tracking-wide">
              <i className="ri-filter-3-line"></i>
              Filter
            </span>
            <select
              value={filterCreator}
              onChange={(e) => setFilterCreator(e.target.value)}
              className="px-3 py-1.5 bg-transparent border border-earth-200 rounded-lg text-sm text-earth-700 focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
            >
              <option value="">All teachers</option>
              {creatorOptions.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <select
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              className="px-3 py-1.5 bg-transparent border border-earth-200 rounded-lg text-sm text-earth-700 focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
            >
              <option value="">All classes</option>
              {classFilterOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {(filterCreator || filterClass) && (
              <button
                onClick={() => {
                  setFilterCreator("");
                  setFilterClass("");
                }}
                className="inline-flex items-center gap-1 text-sm text-earth-700 hover:text-earth-700"
              >
                <i className="ri-close-line"></i>
                Clear
              </button>
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl border border-earth-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-earth-50 border-b border-earth-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Title
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide hidden sm:table-cell">
                    Type
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Scope
                  </th>
                  {isDirector && (
                    <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide hidden md:table-cell">
                      Added by
                    </th>
                  )}
                  <th className="text-left px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide hidden md:table-cell">
                    Link
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-earth-600 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-earth-100">
                {filteredMaterials.map((m) => (
                  <tr key={m.id} className="hover:bg-earth-50/50">
                    <td className="px-4 py-3 text-earth-800 font-medium">
                      {m.title}
                    </td>
                    <td className="px-4 py-3 text-earth-600 capitalize hidden sm:table-cell">
                      {m.materialType}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-olive-100 text-olive-700 rounded text-xs font-medium">
                        {describeScope(m)}
                      </span>
                    </td>
                    {isDirector && (
                      <td className="px-4 py-3 text-earth-600 text-sm hidden md:table-cell">
                        {m.createdByName ?? "—"}
                      </td>
                    )}
                    <td className="px-4 py-3 hidden md:table-cell">
                      <a
                        href={m.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm inline-flex items-center gap-1"
                      >
                        <i className="ri-external-link-line"></i>
                        Open
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(m)}
                          className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <i className="ri-edit-line text-earth-500"></i>
                        </button>
                        <button
                          onClick={() => setToDelete(m)}
                          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-red-500"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredMaterials.length === 0 && (
            <div className="text-center py-12 text-earth-700">
              {(materials?.length ?? 0) === 0
                ? "No extra materials yet. Publish one to get started."
                : "No materials match the selected filters."}
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/30" onClick={closeModal}></div>
          <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-earth-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-earth-800">
                {isEditing ? "Edit Extra Material" : "New Extra Material"}
              </h2>
              <button
                onClick={closeModal}
                className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-xl text-earth-500"></i>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, title: e.target.value }))
                  }
                  placeholder="e.g. Present Perfect — extra practice"
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Link (Drive / activity site)
                </label>
                <input
                  type="url"
                  value={form.externalUrl}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, externalUrl: e.target.value }))
                  }
                  placeholder="https://drive.google.com/..."
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Type
                  </label>
                  <select
                    value={form.materialType}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        materialType: e.target
                          .value as CreateExtraMaterialRequest["materialType"],
                      }))
                    }
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  >
                    <option value="homework">Homework</option>
                    <option value="slides">Extra slides</option>
                    <option value="resource">Resource</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Scope
                  </label>
                  <select
                    value={form.scope}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        scope: e.target
                          .value as CreateExtraMaterialRequest["scope"],
                      }))
                    }
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                  >
                    {isDirector && (
                      <option value="all">All students</option>
                    )}
                    <option value="level">All classes of a level</option>
                    <option value="class">Specific class</option>
                  </select>
                </div>
              </div>

              {/* Scope target */}
              {form.scope === "level" && (
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Level
                  </label>
                  <select
                    value={form.levelCode ?? ""}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, levelCode: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
                    required
                  >
                    <option value="">Select a level</option>
                    {levelCodes.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {form.scope === "class" && (
                <div>
                  <label className="block text-sm font-medium text-earth-700 mb-1">
                    Classes
                  </label>
                  <SearchableSelect
                    items={classItems.filter(
                      (i) => !(form.classGroups ?? []).includes(i.id),
                    )}
                    value=""
                    onChange={(id) => {
                      if (!id) return;
                      setForm((p) => ({
                        ...p,
                        classGroups: [...(p.classGroups ?? []), id],
                      }));
                    }}
                    emptyLabel="Add a class..."
                  />
                  {(form.classGroups ?? []).length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {(form.classGroups ?? []).map((id) => {
                        const item = classItems.find((c) => c.id === id);
                        return (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-olive-100 text-olive-700 rounded text-xs font-medium"
                          >
                            {item?.label ?? id}
                            <button
                              type="button"
                              onClick={() =>
                                setForm((p) => ({
                                  ...p,
                                  classGroups: (p.classGroups ?? []).filter(
                                    (x) => x !== id,
                                  ),
                                }))
                              }
                              className="hover:text-olive-900"
                              title="Remove"
                            >
                              <i className="ri-close-line"></i>
                            </button>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <p className="text-[11px] text-earth-700 mt-1">
                    Você pode adicionar mais de uma turma.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-earth-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, description: e.target.value }))
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500 resize-none"
                />
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="flex-1 px-4 py-2.5 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {isSaving
                    ? "Saving..."
                    : isEditing
                      ? "Save Changes"
                      : "Publish"}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2.5 text-earth-600 hover:text-earth-800 text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {toDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setToDelete(null)}
          ></div>
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-xl p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-xl text-red-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-earth-800 mb-2">
              Delete Material?
            </h3>
            <p className="text-sm text-earth-700 mb-6">
              <strong>{toDelete.title}</strong>
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDelete}
                disabled={deleteMaterial.isPending}
                className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              >
                {deleteMaterial.isPending ? "Deleting..." : "Delete"}
              </button>
              <button
                onClick={() => setToDelete(null)}
                className="flex-1 px-4 py-2.5 border border-earth-200 text-earth-600 hover:bg-earth-50 rounded-lg text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminExtraMaterialsPage;
